import React, { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { Card, Radio, Spin } from "antd";
import { useStoreUpdaterWithAPI } from "../../Hooks/UseStoreUpdaterWithApi";
import { getAdjustedDate } from "./CommonFunctions";
import { adjustTimeByFiveHoursThirtyMinutes } from "../../Utils/DateConverstion";
import { APIConstants } from "../../Common/APIConstants";

const DashSankey = ({activeTab}) => {
  const chartRef = useRef(null);
  const { makeRequest } = useStoreUpdaterWithAPI();
  const [sankeyData, setSankeyData] = useState({ nodes: [], links: [] });
  const correctDate = new Date(getAdjustedDate());
  const[isLoading,setIsLoading]=useState(false)
  const defaultFromDate = dayjs()
    .subtract(1, "day")
    .hour(6)
    .minute(0)
    .second(0);
  const defaultToDate = dayjs().hour(6).minute(0).second(0);
  const [toDate, setToDate] = useState(defaultToDate);
  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [inputType,setInputType]=useState(0)
  const mode = useSelector((state) => state.darkMode);

  const { isdarkMode } = mode;
  const color = useSelector((state) => state.ThemeChange);
  // console.log(color);
  const colors = ["#59c3c9", "#696969", "#C8ff08", "#005E60"];
  const [selectedOption, setSelectedOption] = useState("1");

const handleRadioChange = (e) => {
  const selectedValue = e.target.value;
  setSelectedOption(selectedValue);
  // console.log(selectedValue)
  if(selectedValue==1){
    setInputType(0)
  }else{
    setInputType(1)
  }

  if (selectedValue == 1) {
    setFromDate(dayjs().subtract(1, "day").hour(6).minute(0).second(0));
    setToDate(dayjs().hour(6).minute(0).second(0));

  } else if (selectedValue == 2) {
    setFromDate(dayjs().startOf("month").hour(6).minute(0).second(0));
    setToDate(dayjs().hour(6).minute(0).second(0));
  }
};

  useEffect(() => {

    const colorList = ["#59c3c9", "#696969", "#C8ff08", "#005E60"]; // Ordered colors

    const fetchData = async () => {
      setIsLoading(true)
      const inputData = {
        FromDate: adjustTimeByFiveHoursThirtyMinutes(fromDate),
        ToDate: adjustTimeByFiveHoursThirtyMinutes(toDate),
      };
      console.log(inputType)

      const response = await makeRequest({
        type: APIConstants.GET,
        url: activeTab==="3"?`${APIConstants.DASHBOARD_SANKEY}/${inputData.FromDate}/${inputData.ToDate}/${inputType}`:`${APIConstants.DASHBOARD_SANKEY_GAS}/${inputData.FromDate}/${inputData.ToDate}/${inputType}`,
      });
      console.log(response);

      const nodesSet = new Set();
      const links = [];

      const targetColorMap = new Map();
      let colorIndex = 0;

      response?.forEach((item) => {
        const source = item.source || "Total";
        const target = item.target || "Total";
        const value = item.consumption;

        nodesSet.add(source);
        nodesSet.add(target);

        // Assign a color in order for each new target
        if (!targetColorMap.has(target)) {
          const color = colorList[colorIndex % colorList.length];
          targetColorMap.set(target, color);
          colorIndex++;
        }

        links.push({
          source,
          target,
          value,
          lineStyle: { color: targetColorMap.get(target) },
        });
      });

      const nodes = Array.from(nodesSet).map((name) => ({ name }));
      setSankeyData({ nodes, links });
      setIsLoading(false)
    };

    fetchData();
  }, [inputType]);

useEffect(() => {
    if (!chartRef.current) return;

    const chart = echarts.init(chartRef.current);

    const labelColor = isdarkMode ? "#fff" : "#000";

    // Detect first level and root total
    const rootName = sankeyData.nodes?.[0]?.name;
    const rootTotal = sankeyData.links
      .filter((l) => l.source === rootName)
      .reduce((sum, l) => sum + (l.value || 0), 0);

    // Detect leaf nodes (no outgoing edges)
    const sourceNodes = new Set(sankeyData.links.map((l) => l.source));
    const leafNodes = sankeyData.nodes
      .filter((node) => !sourceNodes.has(node.name))
      .map((node) => node.name);

    const allValuesZero = sankeyData.links.every(
      (l) => (l.value ?? 0) === 0
    );

    if (allValuesZero) {
      chart.setOption({
        graphic: {
          elements: [
            {
              type: "text",
              left: "center",
              top: "middle",
              style: {
                text: "No data available",
                fontSize: 20,
                fill: isdarkMode ? "#aaa" : "#555",
              },
            },
          ],
        },
      });
    } else {
      chart.setOption({
        tooltip: { trigger: "item", triggerOn: "mousemove" },
        animationDuration: 800,
        series: [
          {
            type: "sankey",
            data: sankeyData.nodes,
            links: sankeyData.links,
            layout: "none",
            emphasis: { focus: "adjacency" },
            lineStyle: { curveness: 0.5 },
            label: {
              color: labelColor,
              // fontWeight: "bold",
              formatter: (params) => {
                const name = params.name;
                const value = params.value || 0;

                if (!rootTotal) return `${name} ${value}`;

                if (leafNodes.includes(name)) {
                  const percent = Math.min(100,((value / rootTotal) * 100)).toFixed(1);

                  // const percent = ((value / rootTotal) * 100).toFixed(1);
                  return `${name} ${value} (${percent}%)`;
                }

                return `${name} ${value}`;
              },
            },
          },
        ],
      });
    }

    const resizeObserver = new ResizeObserver(() => chart.resize());
    resizeObserver.observe(chartRef.current);

    return () => {
      resizeObserver.disconnect();
      chart.dispose();
    };
  }, [sankeyData, isdarkMode]);


  console.log(sankeyData);

  return (
        <Card
      title={
        <div className={`${isdarkMode?'text-white':'text-black'}`}>
        {activeTab==="3"?" Nemak Aluminium – Energy Consumption Statistics":"Nemak Aluminium – Gas Consumption Statistics"}
          {/* Energy Consumption Statistics */}
        </div>
      }
      hoverable
      className="shadow-md rounded-xl border max-h-[75vh]"
      style={{
        background: isdarkMode ? "#1f2937" : "#ffffff",
        color: isdarkMode ? "white" : "black",
      }}
    >
      <div style={{ textAlign: "center", marginBottom: "10px" }} >
        <Radio.Group  onChange={handleRadioChange} value={selectedOption}>
          <Radio className={`${isdarkMode?'text-white':'text-black'}`} value="1">Yesterday Consumption ({activeTab==="3"?"kWh":"Kg"})</Radio>
          <Radio  className={`${isdarkMode?'text-white':'text-black'}`} value="2">MTD Consumption ({activeTab==="3"?"kWh":"Kg"})</Radio>
        </Radio.Group>
      </div>
      
<div
  style={{
    position: "relative",
    width: "100%",
    height: "60vh",
    minHeight: "400px",
    maxHeight: "70vh"
  }}
>
  {isLoading && (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(255,255,255,0.6)",
        zIndex: 10
      }}
    >
      <Spin size="large" />
    </div>
  )}

  <div
    ref={chartRef}
    style={{
      width: "100%",
      height: "100%",
      overflow: "hidden"
    }}
  />
</div>
      {/* <div 
       ref={chartRef} 
       style={{
    width: "100%",
    height: "60vh",
    minHeight: "400px",
    maxHeight: "70vh",
    overflow: "hidden"
  }}></div> */}
    </Card>

  );
};

// const DashSankey = () => {
//   const chartRef = useRef(null);
//   const mode = useSelector((state) => state.darkMode);

//   const { isdarkMode } = mode;
//   const [selectedOption, setSelectedOption] = useState("yesterday");

//   const handleRadioChange = (e) => {
//     console.log("Selected:", e.target.value);
//     setSelectedOption(e.target.value);
//   };

//   // ---------------- Static Sankey Data ----------------
//   const staticSankeyData = {
//     nodes: [
//       { name: "TNEB" },

//       { name: "Shift A" },
//       { name: "Shift B" },
//       { name: "Shift C" },

//       { name: "HPDC" },
//       { name: "GSPM" },
//       { name: "Core Shop" },
//       { name: "Sand Plant" },
//       { name: "Heat Treatment Furnaces" },
//       { name: "Finishing Line" },
//       { name: "Melting" },
//       { name: "Office" },
//       { name: "Utility" },
//       { name: "Others" },
//     ],

//     links: [
//       { source: "TNEB", target: "Shift A", value: 28333.33 },
//       { source: "TNEB", target: "Shift B", value: 28333.33 },
//       { source: "TNEB", target: "Shift C", value: 28333.33 },

//       ...["Shift A", "Shift B", "Shift C"].flatMap((shift) =>
//         [
//           "HPDC",
//           "GSPM",
//           "Core Shop",
//           "Sand Plant",
//           "Heat Treatment Furnaces",
//           "Finishing Line",
//           "Melting",
//           "Office",
//           "Utility",
//           "Others",
//         ].map((grp) => ({
//           source: shift,
//           target: grp,
//           value: 2833.33,
//         }))
//       ),
//     ],
//   };

//   // Compute totals for nodes
//   const calculateNodeTotals = () => {
//     const totals = {};

//     staticSankeyData.nodes.forEach((n) => (totals[n.name] = 0));

//     staticSankeyData.links.forEach((link) => {
//       if (totals[link.target] !== undefined) {
//         totals[link.target] += link.value;
//       }
//     });

//     return totals;
//   };

//   const nodeTotals = calculateNodeTotals();

//   // --------------- ECharts Setup ---------------
//   useEffect(() => {
//     if (!chartRef.current) return;
//     const chart = echarts.init(chartRef.current);

//     const textColor = isdarkMode ? "#ffffff" : "#000000";

//     chart.setOption({
//       title: {
//         text: "Energy Flow Sankey (Static Demo)",
//         left: "center",
//         textStyle: { color: textColor, fontWeight: "bold" },
//       },

//       tooltip: {
//         trigger: "item",
//         formatter: (item) =>
//           item.value
//             ? `${item.data.source} → ${item.data.target}<br><b>${item.value} units</b>`
//             : `${item.name}<br><b>Total: ${nodeTotals[item.name] || 85000} units</b>`,
//       },

//       series: [
//         {
//           type: "sankey",
//           data: staticSankeyData.nodes.map((node) => ({
//             ...node,
//             label: {
//               show: true,
//               formatter: `${node.name} (${nodeTotals[node.name] || 85000})`,
//             },
//           })),

//           links: staticSankeyData.links,
//           emphasis: { focus: "adjacency" },
//           lineStyle: { color: "gradient", curveness: 0.5 },

//           label: {
//             color: textColor,
//             fontSize: 12,
//           },
//         },
//       ],
//     });

//     const resizeObserver = new ResizeObserver(() => chart.resize());
//     resizeObserver.observe(chartRef.current);

//     return () => {
//       resizeObserver.disconnect();
//       chart.dispose();
//     };
//   }, [isdarkMode]);

//   return (
//     <Card
//       title="Sankey Flow Visualization"
//       className="shadow-md rounded-xl border"
//       style={{
//         background: isdarkMode ? "#1f2937" : "#ffffff",
//         color: isdarkMode ? "white" : "black",
//       }}
//     >
//       <div style={{ textAlign: "center", marginBottom: "10px" }}>
//         <Radio.Group onChange={handleRadioChange} value={selectedOption}>
//           <Radio value="yesterday">Yesterday Consumption (kWh)</Radio>
//           <Radio value="mtd">MTD Consumption (kWh)</Radio>
//         </Radio.Group>
//       </div>
//       <div ref={chartRef} style={{ width: "100%", height: "500px" }}></div>
//     </Card>
//   );
// };

export default DashSankey;
